import { Injectable, inject } from '@angular/core'
import { ScriptManagementService } from '@everyday-speech/eds-design-system-lib'
import { posthogScript } from '../../scripts/static-scripts'
import { environment } from '../../../../environments/environment'
import { FeatureFlags } from './feature-flags'
import { FeatureToken } from './feature-token'
import { BehaviorSubject, Observable } from 'rxjs'
import { filter, map } from 'rxjs/operators'
import { PostHog } from './posthog.types'

@Injectable({
  providedIn: 'root',
})
export class FeatureFlaggingService {
  private scriptManager = inject(ScriptManagementService);

  private _flagsAreLoaded = false
  flagsSubject: BehaviorSubject<string[] | null> = new BehaviorSubject<string[] | null>(null)
  flagVariantsSubject: BehaviorSubject<Record<string, unknown> | null> = new BehaviorSubject<Record<string, unknown> | null>(null)

  get flagsAreLoaded() {
    return this._flagsAreLoaded
  }

  private get posthog(): unknown {
    return (window as unknown as { posthog: unknown }).posthog
  }

  init() {
    return new Promise(resolve => {
      const timeoutDuration = 3000
      const timeout = setTimeout(() => {
        if (!this._flagsAreLoaded) {
          this.flagsSubject.next([])
          this.flagVariantsSubject.next({})
          resolve(null)
        }
      }, timeoutDuration)

      this.scriptManager.load(posthogScript).then(() => {
        clearTimeout(timeout)

        try {
          ;(this.posthog as { init: (key: string, config: { api_host: string }) => void }).init(environment.posthogPublicApiKey, {
            api_host: environment.posthogApiHost,
          })
        } catch (error) {
          console.error('Failed to initialize PostHog:', error)
          this.flagsSubject.next([])
          this.flagVariantsSubject.next({})
          resolve(null)
          return
        }

        ;(this.posthog as { onFeatureFlags: (callback: () => void) => void }).onFeatureFlags(() => {
          this._flagsAreLoaded = true
          this.flagsSubject.next((this.posthog as { featureFlags: { getFlags: () => string[] } }).featureFlags.getFlags())
          this.flagVariantsSubject.next(
            (this.posthog as { featureFlags: { getFlagVariants: () => Record<string, unknown> } }).featureFlags.getFlagVariants()
          )
          resolve(this.posthog)
        })
      })
    })
  }

  isFeatureRegistered(featureToken: FeatureToken) {
    return Object.values(FeatureFlags).includes(featureToken)
  }

  isFeatureEnabled(featureToken: FeatureToken): boolean {
    if (!this.isFeatureRegistered(featureToken)) {
      throw new Error(`Feature token "${featureToken}" is not registered`)
    }

    const posthog = this.posthog as PostHog | undefined
    if (!posthog) {
      return false
    }

    const flagValue = posthog.getFeatureFlag(featureToken)
    return flagValue === true
  }

  isFeatureEnabledReactive(featureToken: FeatureToken): Observable<boolean> {
    if (!this.isFeatureRegistered(featureToken)) {
      throw new Error(`Feature token "${featureToken}" is not registered`)
    }

    return this.flagsSubject.pipe(
      filter((flags): flags is string[] => flags !== null && flags !== undefined),
      map(() => this.isFeatureEnabled(featureToken))
    )
  }

  getFeatureFlagVariant(featureToken: FeatureToken) {
    if (!this.isFeatureRegistered(featureToken)) throw new Error(`Feature token "${featureToken}" is not registered`)
    const feature = (this.posthog as { getFeatureFlag?: (token: string) => unknown })?.getFeatureFlag?.(featureToken)
    return feature
  }

  getFeatureFlagPayload(featureToken: FeatureToken) {
    if (!this.isFeatureRegistered(featureToken)) throw new Error(`Feature token "${featureToken}" is not registered`)
    const feature = (this.posthog as { getFeatureFlagPayload?: (token: string) => unknown })?.getFeatureFlagPayload?.(featureToken)
    return feature
  }

  /**
   * Identifies the current user for feature flagging
   * @param user User information including ID and properties
   */
  identifyUser(user: {
    id: string
    email?: string
    accessRole?: string
    belongsToActivePilot?: boolean
    firstName?: string
    lastName?: string
    professionalRole?: string
    status?: string
    daysActive?: number
    license?: string
    adminPortalVersion?: string
  }) {
    const { id, ...properties } = user

    // Use user ID as the distinct identifier for PostHog
    ;(this.posthog as { identify?: (id: string, properties: Record<string, string | number | boolean>) => void })?.identify?.(id, {
      ...properties,
      // Ensure email is always included if available
      ...(properties.email && { email: properties.email }),
      platform: 'admin-portal',
      version: properties.adminPortalVersion || 'v2',
    })
  }

  // Reset User session after logout to prevent mixing users that use the same device
  // https://posthog.com/docs/libraries/js#reset-after-logout
  resetUserSession() {
    ;(this.posthog as { reset?: () => void })?.reset?.()
  }
}
